import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchReports, downloadReport } from '../../api/portal-api.js';
import type { MonthlyReportInfo } from '@qdf/shared';

function fmtCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(amount);
}

export function MonthlyReports() {
  const { data: reports, isLoading } = useQuery<MonthlyReportInfo[]>({
    queryKey: ['portal-reports'],
    queryFn: fetchReports,
  });

  const [downloading, setDownloading] = useState<string | null>(null);

  // Reports are generated per company — accounting gets Left Main and
  // New Western as separate CSVs.
  const handleDownload = async (yearMonth: string, company: 'leftmain' | 'newwestern') => {
    const key = `${yearMonth}:${company}`;
    setDownloading(key);
    try {
      const { downloadUrl } = await downloadReport(yearMonth, company);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `qdf-report-${yearMonth}-${company}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      alert('Failed to generate report. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const companyBtnLabel = (company: 'leftmain' | 'newwestern') =>
    company === 'leftmain' ? 'Left Main CSV' : 'New Western CSV';

  /* ── Loading state ─────────────────────────────────────────── */
  if (isLoading) {
    return (
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: '#64748b', marginTop: '16px', fontSize: '14px' }}>
          Loading reports…
        </p>
      </div>
    );
  }

  /* ── Empty state ───────────────────────────────────────────── */
  if (!reports || !Array.isArray(reports) || reports.length === 0) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <h1 style={s.title}>Monthly Reports</h1>
          <p style={s.subtitle}>Download submission data for accounting</p>
        </div>
        <div style={s.emptyState}>
          <div style={s.emptyIcon}>📊</div>
          <h2 style={s.emptyTitle}>No reports available yet</h2>
          <p style={s.emptyText}>
            Once submissions are processed, monthly reports will appear here for download.
          </p>
        </div>
      </div>
    );
  }

  const reportsList = Array.isArray(reports) ? reports : [];
  const currentMonth = reportsList.find((r) => r.isCurrent);
  const pastMonths = reportsList.filter((r) => !r.isCurrent);

  return (
    <div style={s.page}>
      {/* ── Header ──────────────────────────────────────────── */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Monthly Reports</h1>
          <p style={s.subtitle}>Download submission data for accounting</p>
        </div>
        <div style={s.headerBadge}>
          📊 <span style={{ fontWeight: 600 }}>{reportsList.length}</span>{' '}
          {reportsList.length === 1 ? 'report' : 'reports'} available
        </div>
      </div>

      {/* ── Current Month Card ──────────────────────────────── */}
      {currentMonth && (
        <div style={s.currentCardOuter}>
          <div style={s.currentCard}>
            {/* Shimmer bar */}
            <div style={s.shimmerBar} />

            <div style={s.currentBody}>
              <div style={s.currentTop}>
                <div>
                  <span style={s.currentBadge}>Current Month</span>
                  <h2 style={s.currentMonth}>{currentMonth.label}</h2>
                </div>
                <div style={s.currentIcon}>📊</div>
              </div>

              {/* Stats row */}
              <div style={s.statsRow}>
                <div style={s.statBox}>
                  <div style={s.statLabel}>Submissions</div>
                  <div style={s.statValueLg}>{currentMonth.submissionCount}</div>
                </div>
                <div style={s.statDivider} />
                <div style={s.statBox}>
                  <div style={s.statLabel}>💵 Funded Out</div>
                  <div style={s.statValueGreen}>
                    {fmtCurrency(currentMonth.totalApprovedAmount)}
                  </div>
                </div>
                <div style={s.statDivider} />
                <div style={s.statBox}>
                  <div style={s.statLabel}>💰 Funded In</div>
                  <div style={s.statValueTeal}>
                    {fmtCurrency(currentMonth.totalFundedAmount)}
                  </div>
                </div>
              </div>

              {/* Download buttons — one CSV per company */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' as const }}>
                {(['leftmain', 'newwestern'] as const).map((company) => (
                  <button
                    key={company}
                    style={{
                      ...s.downloadBtnLg,
                      flex: 1,
                      opacity: downloading === `${currentMonth.yearMonth}:${company}` ? 0.7 : 1,
                      cursor: downloading ? 'not-allowed' : 'pointer',
                    }}
                    disabled={!!downloading}
                    onClick={() => handleDownload(currentMonth.yearMonth, company)}
                  >
                    {downloading === `${currentMonth.yearMonth}:${company}` ? (
                      <>
                        <span style={s.btnSpinner} />
                        Generating Report…
                      </>
                    ) : (
                      <>⬇ {companyBtnLabel(company)}</>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Past Months Grid ────────────────────────────────── */}
      {pastMonths.length > 0 && (
        <>
          <h2 style={s.sectionTitle}>Past Reports</h2>
          <div style={s.pastGrid}>
            {pastMonths.map((report) => (
              <div key={report.yearMonth} style={s.pastCard}>
                <div style={s.pastCardHeader}>
                  <div>
                    <div style={s.pastMonth}>{report.label}</div>
                    <div style={s.pastSubmissions}>
                      {report.submissionCount}{' '}
                      {report.submissionCount === 1 ? 'submission' : 'submissions'}
                    </div>
                  </div>
                  <div style={s.pastIcon}>📄</div>
                </div>

                <div style={s.pastStats}>
                  <div style={s.pastStatItem}>
                    <span style={s.pastStatLabel}>Funded Out</span>
                    <span style={s.pastStatValue}>
                      {fmtCurrency(report.totalApprovedAmount)}
                    </span>
                  </div>
                  <div style={s.pastStatItem}>
                    <span style={s.pastStatLabel}>Funded In</span>
                    <span style={s.pastStatValue}>
                      {fmtCurrency(report.totalFundedAmount)}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['leftmain', 'newwestern'] as const).map((company) => (
                    <button
                      key={company}
                      style={{
                        ...s.downloadBtn,
                        flex: 1,
                        opacity: downloading === `${report.yearMonth}:${company}` ? 0.7 : 1,
                        cursor: downloading ? 'not-allowed' : 'pointer',
                      }}
                      disabled={!!downloading}
                      onClick={() => handleDownload(report.yearMonth, company)}
                    >
                      {downloading === `${report.yearMonth}:${company}` ? (
                        <>
                          <span style={s.btnSpinnerSm} />
                          Generating…
                        </>
                      ) : (
                        <>⬇ {companyBtnLabel(company)}</>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Styles
   ═══════════════════════════════════════════════════════════════ */

const s: Record<string, React.CSSProperties> = {
  /* Page */
  page: {
    padding: '24px 32px',
    maxWidth: '1200px',
    margin: '0 auto',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },

  /* Header */
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    margin: '3px 0 0',
  },
  headerBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#475569',
  },

  /* Loading */
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid #e2e8f0',
    borderTop: '3px solid #4338ca',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },

  /* Empty */
  emptyState: {
    textAlign: 'center' as const,
    padding: '80px 40px',
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
  },
  emptyIcon: {
    fontSize: '56px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#94a3b8',
    maxWidth: '360px',
    margin: '0 auto',
    lineHeight: '1.6',
  },

  /* ── Current Month Card ──────────────────────────────────── */
  currentCardOuter: {
    background: 'linear-gradient(135deg, #4338ca, #6366f1, #059669)',
    borderRadius: '18px',
    padding: '2px',
    marginBottom: '32px',
  },
  currentCard: {
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    borderRadius: '16px',
    overflow: 'hidden',
  },
  shimmerBar: {
    height: '4px',
    background:
      'linear-gradient(90deg, #4338ca, #6366f1, #818cf8, #059669, #10b981, #6366f1, #4338ca)',
    backgroundSize: '300% 100%',
    animation: 'shimmer 3s ease-in-out infinite',
  },
  currentBody: {
    padding: '28px 32px',
  },
  currentTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  currentBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: 'rgba(99, 102, 241, 0.2)',
    border: '1px solid rgba(99, 102, 241, 0.3)',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#a5b4fc',
    letterSpacing: '0.3px',
    marginBottom: '8px',
  },
  currentMonth: {
    fontSize: '32px',
    fontWeight: 800,
    color: '#fff',
    margin: 0,
    letterSpacing: '-0.5px',
  },
  currentIcon: {
    fontSize: '48px',
    opacity: 0.3,
  },

  /* Stats row */
  statsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    marginBottom: '24px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    padding: '16px 0',
  },
  statBox: {
    flex: 1,
    textAlign: 'center' as const,
    padding: '0 16px',
  },
  statDivider: {
    width: '1px',
    height: '40px',
    background: 'linear-gradient(to bottom, transparent, #334155, transparent)',
  },
  statLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '6px',
  },
  statValueLg: {
    fontSize: '36px',
    fontWeight: 800,
    color: '#fff',
    letterSpacing: '-1px',
    lineHeight: 1,
  },
  statValueGreen: {
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '-0.5px',
    lineHeight: 1,
    background: 'linear-gradient(135deg, #34d399, #059669)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  } as React.CSSProperties,
  statValueTeal: {
    fontSize: '22px',
    fontWeight: 800,
    letterSpacing: '-0.5px',
    lineHeight: 1,
    background: 'linear-gradient(135deg, #5eead4, #14b8a6)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  } as React.CSSProperties,

  /* Download button — large (current month) */
  downloadBtnLg: {
    width: '100%',
    padding: '14px 24px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: '#fff',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    letterSpacing: '-0.2px',
    transition: 'all 0.2s',
    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.3)',
  },

  /* ── Past months ─────────────────────────────────────────── */
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 16px',
  },
  pastGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '16px',
  },
  pastCard: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.2s, border-color 0.2s',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
  },
  pastCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  pastMonth: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1e293b',
    letterSpacing: '-0.3px',
  },
  pastSubmissions: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: '2px',
  },
  pastIcon: {
    fontSize: '28px',
    opacity: 0.4,
  },

  /* Past stats */
  pastStats: {
    display: 'flex',
    gap: '16px',
  },
  pastStatItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px',
    padding: '10px 12px',
    background: '#f8fafc',
    borderRadius: '8px',
  },
  pastStatLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#94a3b8',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  pastStatValue: {
    fontSize: '15px',
    fontWeight: 700,
    color: '#1e293b',
  },

  /* Download button — small (past months) */
  downloadBtn: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(5, 150, 105, 0.2)',
  },

  /* Spinner for buttons */
  btnSpinner: {
    display: 'inline-block',
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  btnSpinnerSm: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid #fff',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};
